/**
 * @file api.ts
 * @description
 * -----------------------------------------------------
 * Funciones para interactuar con la API de usuarios
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2026-04-10
 * @author DLTCode
 * @author Sergio Manjón
 * @copyright © DLTCode
 * @license Private
 * */
import { fetchWithToken } from "@/lib/fetch";
import { addPaginationToUrl } from "@/lib/paginationQuery";
import { GetPaginatedDataResponse } from "@/types/fetch/fetchPaginatedData";
import { PaginationQuery } from "@/types/pagination";
import {
  AllUsers,
  BlockUserResponse,
  BlockUnblockUser,
  MessageResponse,
  ResendInviteBody,
  InviteBody,
} from "@/types/user/user";
import { DeleteUser, UpdateUser } from "./types";

/**
 * Realiza la solicitud de traer todos los usuarios dentro de la plataforma
 */
export const fetchAllUsers = (
  paginationData?: PaginationQuery
): Promise<FetchResponse<GetPaginatedDataResponse<AllUsers>>> => {
  const url = addPaginationToUrl("user/users-summary", paginationData);
  return fetchWithToken<GetPaginatedDataResponse<AllUsers>, undefined>(
    url,
    "GET",
    undefined
  );
};

/**
 * Realiza la función de eliminar a un usuario dentro de la plataforma
 */
export const fetchDeleteUser = (
  data: DeleteUser
): Promise<FetchResponse<MessageResponse>> => {
  return fetchWithToken<MessageResponse, number[]>(
    "user/delete-user",
    "DELETE",
    data.ids
  );
};

/**
 * Realiza la función de blockear a un usuario dentro de la plataforma
 */
export const fetchToggleBlockUsers = (
  body: BlockUnblockUser
): Promise<FetchResponse<BlockUserResponse>> => {
  return fetchWithToken<BlockUserResponse, BlockUnblockUser>(
    "user/toggle-block",
    "PATCH",
    body
  );
};

/**
 * Realiza la función de reenviar la invitación a un usuario en espera
 */
export const fetchResendInvite = (
  data: ResendInviteBody
): Promise<FetchResponse<MessageResponse>> => {
  return fetchWithToken<MessageResponse, ResendInviteBody>(
    "auth/resend-invite",
    "POST",
    data
  );
};

/**
 * Realiza la función de invitar a un usuario a la plataforma
 */
export const fetchInviteUser = (
  data: InviteBody
): Promise<FetchResponse<MessageResponse>> => {
  return fetchWithToken<MessageResponse, InviteBody>(
    "auth/invite",
    "POST",
    data
  );
};

/**
 * Realiza la función de actualizar a un usuario dentro de la plataforma
 */
export const fetchUpdateUser = (
  data: UpdateUser,
  id: number
): Promise<FetchResponse<MessageResponse>> => {
  return fetchWithToken<MessageResponse, UpdateUser>(
    `user/update-user-admin/${id}`,
    "PUT",
    data
  );
};
